import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Divider, LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ShoppingCart as CartIcon,
  Download as DownloadIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  LocalOffer as OfferIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { toast } from 'react-toastify';
import api from '../../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState(0);
  const [analytics, setAnalytics] = useState({
    dailyVisitors: [],
    behaviorDistribution: [],
    conversionFunnel: { viewers: 0, cart_adders: 0, purchasers: 0 },
    topCategories: []
  });
  const [topSearches, setTopSearches] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [remarketing, setRemarketing] = useState({ items: [], pagination: {} });
  const [remarketingType, setRemarketingType] = useState('recent');

  useEffect(() => {
    fetchData();
  }, [days]);

  useEffect(() => {
    if (tab === 2) {
      fetchRemarketing();
    }
  }, [tab, remarketingType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, searchesRes, productsRes] = await Promise.all([
        api.get(`/analytics/admin/dashboard?days=${days}`),
        api.get(`/analytics/admin/top-searches?days=${days}`),
        api.get(`/analytics/admin/top-products?days=${days}`)
      ]);
      setAnalytics(analyticsRes.data);
      setTopSearches(searchesRes.data);
      setTopProducts(productsRes.data);
    } catch (error) {
      console.error('取得分析數據失敗:', error);
      toast.error('取得數據失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchRemarketing = async () => {
    try {
      const response = await api.get(`/analytics/admin/remarketing?type=${remarketingType}`);
      setRemarketing(response.data);
    } catch (error) {
      console.error('取得再行銷名單失敗:', error);
    }
  };

  const handleExportRemarketing = async () => {
    try {
      const response = await api.get(`/analytics/admin/remarketing/export?type=${remarketingType}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'remarketing_list.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('匯出失敗:', error);
      toast.error('匯出失敗');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('zh-TW');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('zh-TW');
  };

  // 計算轉換率
  const calcConversionRate = (from, to) => {
    if (!from || from === 0) return 0;
    return ((to / from) * 100).toFixed(1);
  };

  const { conversionFunnel } = analytics;
  const viewToCartRate = calcConversionRate(conversionFunnel.viewers, conversionFunnel.cart_adders);
  const cartToPurchaseRate = calcConversionRate(conversionFunnel.cart_adders, conversionFunnel.purchasers);
  const overallRate = calcConversionRate(conversionFunnel.viewers, conversionFunnel.purchasers);

  return (
    <Box sx={{ p: 3 }}>
      {/* 頂部標題 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon /> 數據分析中心
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>時間範圍</InputLabel>
            <Select value={days} label="時間範圍" onChange={(e) => setDays(e.target.value)}>
              <MenuItem value={1}>今天</MenuItem>
              <MenuItem value={7}>近 7 天</MenuItem>
              <MenuItem value={30}>近 30 天</MenuItem>
              <MenuItem value={90}>近 90 天</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>
            重新整理
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/admin/orders')}
          >
            訂單管理
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* 摘要卡片 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {analytics.dailyVisitors?.reduce((sum, d) => sum + d.visitors, 0) || 0}
                  </Typography>
                  <Typography color="text.secondary">總訪客數</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ViewIcon sx={{ fontSize: 40, color: '#388e3c' }} />
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {conversionFunnel.viewers || 0}
                  </Typography>
                  <Typography color="text.secondary">瀏覽用戶</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CartIcon sx={{ fontSize: 40, color: '#f57c00' }} />
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {conversionFunnel.cart_adders || 0}
                  </Typography>
                  <Typography color="text.secondary">加購物車</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#fce4ec' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <OfferIcon sx={{ fontSize: 40, color: '#c2185b' }} />
                  <Typography variant="h4" fontWeight="bold" color="error">
                    {conversionFunnel.purchasers || 0}
                  </Typography>
                  <Typography color="text.secondary">完成購買</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 分頁 */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              <Tab icon={<TrendingUpIcon />} label="流量分析" />
              <Tab icon={<SearchIcon />} label="搜尋/商品" />
              <Tab icon={<PeopleIcon />} label="再行銷名單" />
            </Tabs>
          </Paper>

          {/* Tab 0: 流量分析 */}
          {tab === 0 && (
            <Grid container spacing={3}>
              {/* 每日訪客圖表 */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>每日訪客趨勢</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.dailyVisitors}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(d) => d?.substring(5)} />
                      <YAxis />
                      <Tooltip labelFormatter={(d) => `日期: ${d}`} />
                      <Legend />
                      <Line type="monotone" dataKey="visitors" name="訪客數" stroke="#1976d2" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* 行為分布 */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>行為分布</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.behaviorDistribution}
                        dataKey="count"
                        nameKey="behavior_type"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ behavior_type, percent }) =>
                          `${behavior_type} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {analytics.behaviorDistribution.map((entry, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* 轉換漏斗 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>轉換漏斗</Typography>
                  <Box sx={{ my: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography>瀏覽 → 加購物車</Typography>
                        <Typography fontWeight="bold">{viewToCartRate}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(viewToCartRate), 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                        color="info"
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography>購物車 → 購買</Typography>
                        <Typography fontWeight="bold">{cartToPurchaseRate}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(cartToPurchaseRate), 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                        color="warning"
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography>整體轉換率</Typography>
                        <Typography fontWeight="bold" color="error">{overallRate}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(overallRate), 100)}
                        sx={{ height: 10, borderRadius: 5 }}
                        color="error"
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* 熱門分類 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>熱門分類</Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.topCategories} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={100} />
                      <Tooltip />
                      <Bar dataKey="views" fill="#8884d8" name="瀏覽次數" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: 搜尋與商品 */}
          {tab === 1 && (
            <Grid container spacing={3}>
              {/* 熱門搜尋 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon /> 熱門搜尋關鍵字
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>排名</TableCell>
                          <TableCell>關鍵字</TableCell>
                          <TableCell align="right">搜尋次數</TableCell>
                          <TableCell align="right">平均結果數</TableCell>
                          <TableCell align="right">轉換</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topSearches.map((item, index) => (
                          <TableRow key={item.keyword} hover>
                            <TableCell>
                              <Chip
                                size="small"
                                label={index + 1}
                                color={index < 3 ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                                {item.keyword}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.total_searches}</TableCell>
                            <TableCell align="right">
                              {Math.round(item.avg_results) || '-'}
                            </TableCell>
                            <TableCell align="right">
                              {item.conversions || 0}
                            </TableCell>
                          </TableRow>
                        ))}
                        {topSearches.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              暫無搜尋數據
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* 熱門商品 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ViewIcon /> 熱門瀏覽商品
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>商品</TableCell>
                          <TableCell align="right">瀏覽</TableCell>
                          <TableCell align="right">加購</TableCell>
                          <TableCell align="right">購買</TableCell>
                          <TableCell align="right">轉換率</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topProducts.map((item) => (
                          <TableRow key={item.product_id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {item.image_url && (
                                  <Box
                                    component="img"
                                    src={item.image_url}
                                    alt={item.name}
                                    sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                                  />
                                )}
                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                  {item.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{item.total_views}</TableCell>
                            <TableCell align="right">{item.total_carts}</TableCell>
                            <TableCell align="right">{item.total_purchases}</TableCell>
                            <TableCell align="right">
                              <Chip
                                size="small"
                                label={`${item.cart_rate || 0}%`}
                                color={item.cart_rate > 10 ? 'success' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        {topProducts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              暫無商品數據
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 2: 再行銷名單 */}
          {tab === 2 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon /> 再行銷名單
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>名單類型</InputLabel>
                    <Select
                      value={remarketingType}
                      label="名單類型"
                      onChange={(e) => setRemarketingType(e.target.value)}
                    >
                      <MenuItem value="recent">最近訪問 (24小時)</MenuItem>
                      <MenuItem value="abandoned">棄單用戶</MenuItem>
                      <MenuItem value="with_phone">有電話用戶</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportRemarketing}
                  >
                    匯出 CSV
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Session ID</TableCell>
                      <TableCell>電話</TableCell>
                      <TableCell>最後搜尋</TableCell>
                      <TableCell>最後瀏覽商品</TableCell>
                      <TableCell align="right">訪問次數</TableCell>
                      <TableCell align="right">購物車金額</TableCell>
                      <TableCell>棄單</TableCell>
                      <TableCell>最後訪問</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {remarketing.items?.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {item.session_id?.substring(0, 16)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.user_phone ? (
                            <Chip size="small" label={item.user_phone} color="primary" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{item.last_search || '-'}</TableCell>
                        <TableCell>
                          {item.last_product_name ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.last_product_image && (
                                <Box
                                  component="img"
                                  src={item.last_product_image}
                                  alt=""
                                  sx={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 1 }}
                                />
                              )}
                              <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                {item.last_product_name}
                              </Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">{item.visit_count || 1}</TableCell>
                        <TableCell align="right">
                          {item.cart_value > 0 ? (
                            <Typography color="error" fontWeight="bold">
                              NT${item.cart_value}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {item.cart_abandoned ? (
                            <Chip size="small" label="是" color="error" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDateTime(item.last_visit_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!remarketing.items || remarketing.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">暫無數據</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {remarketing.pagination?.total > 0 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    共 {remarketing.pagination.total} 筆資料
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default AdminAnalytics;
